<template>
  <section class="contact-page">
    <div class="contact-page-container">
      <div class="contact-wave" aria-hidden="true" />

      <!-- 顶部：联系信息 + 地图 -->
      <div class="contact-top container--wide">
        <header class="contact-top__head" data-aos="fade-up">
          <h1 class="contact-top__title">联系我们</h1>
          <p class="contact-top__eyebrow">CONTACT US</p>
        </header>

        <div class="contact-top__grid" data-aos="fade-up" data-aos-delay="80">
          <div class="contact-top__building">
            <LazyBg :src="buildingBg" eager />
          </div>

          <div class="contact-top__aside">
            <a
              class="contact-map-link"
              :href="mapSearchUrl"
              target="_blank"
              rel="noopener noreferrer"
              :title="`在地图中打开：${ADDRESS}`"
              :aria-label="`在地图中查看总部位置：${ADDRESS}`"
            >
              <img
                class="contact-map-link__img"
                src="@/assets/Rectangle35.png"
                width="100%"
                alt="公司位置地图预览，点击查看导航"
                loading="lazy"
                decoding="async"
              />
              <span class="contact-map-link__badge" aria-hidden="true">
                <svg class="contact-map-link__icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 21s7-4.35 7-10a7 7 0 1 0-14 0c0 5.65 7 10 7 10Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <circle cx="12" cy="11" r="2.5" fill="currentColor" />
                </svg>
                点击查看地图
              </span>
            </a>

            <ul class="contact-info" role="list">
              <li v-for="item in contactItems" :key="item.title" class="contact-info__row">
                <span class="contact-info__icon" aria-hidden="true">
                  <component :is="item.icon" />
                </span>
                <div class="contact-info__body">
                  <strong class="contact-info__title">{{ item.title }}</strong>
                  <p class="contact-info__text">{{ item.text }}</p>
                </div>
              </li>
            </ul>

            <p class="contact-visit-note">
              如需到访总部或预约参观，请提前通过商务热线或在线留言与我们联系，我们将为您安排对接人与接待事宜。
            </p>
          </div>
        </div>
      </div>

      <!-- 底部：留言表单 -->
      <div class="contact-form-section container--wide">
        <header class="contact-form-section__head" data-aos="fade-up">
          <h2 class="contact-form-section__title">如果您有任何疑问，欢迎在此留言</h2>
          <p class="contact-form-section__desc">
            无论您是寻求AI解决方案定制、产品采购、生态共建，还是技术合作、人才加盟，均可通过以下方式快速对接
          </p>
        </header>

        <div class="contact-form-section__grid" data-aos="fade-up" data-aos-delay="80">
          <form class="inquiry-form" @submit.prevent="onSubmit">
            <div class="inquiry-row">
              <label class="inquiry-label" for="inquiry-name">
                <span class="inquiry-required" aria-hidden="true">*</span>姓名：
              </label>
              <input id="inquiry-name" v-model.trim="form.name" type="text" class="inquiry-input" maxlength="40"
                autocomplete="name" />
            </div>

            <div class="inquiry-row">
              <label class="inquiry-label" for="inquiry-company">单位/职位：</label>
              <input id="inquiry-company" v-model.trim="form.company" type="text" class="inquiry-input" maxlength="80"
                autocomplete="organization" />
            </div>

            <div class="inquiry-row">
              <label class="inquiry-label" for="inquiry-phone">
                <span class="inquiry-required" aria-hidden="true">*</span>联系电话：
              </label>
              <input id="inquiry-phone" v-model.trim="form.phone" type="tel" class="inquiry-input" maxlength="20"
                autocomplete="tel" />
            </div>

            <div class="inquiry-row inquiry-row--types">
              <span class="inquiry-label">咨询类型：</span>
              <div class="inquiry-types" role="group" aria-label="咨询类型">
                <label v-for="opt in inquiryTypeOptions" :key="opt" class="inquiry-check">
                  <input v-model="form.inquiryTypes" type="checkbox" :value="opt" class="inquiry-check-input" />
                  <span class="inquiry-check-box" aria-hidden="true" />
                  <span class="inquiry-check-text">{{ opt }}</span>
                </label>
              </div>
            </div>

            <div class="inquiry-row inquiry-row--desc">
              <label class="inquiry-label" for="inquiry-desc">需求描述：</label>
              <textarea id="inquiry-desc" v-model.trim="form.description" class="inquiry-textarea" rows="4"
                maxlength="500" />
            </div>

            <p v-if="formError" class="inquiry-feedback inquiry-feedback--error" role="alert">{{ formError }}</p>
            <p v-if="formSuccess" class="inquiry-feedback inquiry-feedback--success" role="status">{{ formSuccess }}</p>

            <div class="inquiry-actions">
              <button type="submit" class="inquiry-submit" :disabled="submitting">
                {{ submitting ? '提交中…' : '提交' }}
              </button>
            </div>

            <p class="inquiry-privacy">
              您提供的联系信息仅用于业务对接与沟通，海曦技术将严格遵守《数据安全法》《个人信息保护法》，保障信息安全与隐私，绝不泄露给第三方。
            </p>
          </form>

          <div class="contact-form-visual">
            <div class="contact-placeholder contact-placeholder--handshake">
              <img class="contact-handshake__img" src="@/assets/buildShip.png" alt="" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import buildingBg from '@/assets/building.jpg';
import LazyBg from '@/components/LazyBg.vue';
import { h, reactive, ref } from 'vue';
import { fetchContactUsMessages } from '@/api/front';
import { useSeo } from '@/composables/useSeo';

const inquiryTypeOptions = ['政企解决方案', '产品采购', '生态合作', '人才招聘', '其他'] as const;

const ADDRESS = '上海市浦东新区盛夏路61弄1号楼501室';
const TRANSPORT = '地铁2号线广兰路站，步行15分钟；公交';
const PHONE_LINE = '400-001-5020，工作日 9:30–18:30（法定节假日除外）';

/** 高德地图：按地址搜索，移动端尝试唤起 App */
const mapSearchUrl = `https://uri.amap.com/search?keyword=${encodeURIComponent(ADDRESS)}&callnative=1`;

const IconBuilding = () =>
  h(
    'svg',
    {
      xmlns: 'http://www.w3.org/2000/svg',
      width: '24',
      height: '24',
      viewBox: '0 0 24 24',
      fill: 'none'
    },
    [
      h('path', {
        d: 'M8.5 6L2 3V18L8.5 21L15.5 18L22 21V6L15.5 3L8.5 6Z',
        stroke: '#0095FF',
        'stroke-width': '1.5',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
      }),
      h('path', {
        d: 'M15.5 3V18',
        stroke: '#0095FF',
        'stroke-width': '1.5',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
      }),
      h('path', {
        d: 'M8.5 6V21',
        stroke: '#0095FF',
        'stroke-width': '1.5',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
      }),
      h('path', {
        d: 'M5.25 4.5L8.5 6L15.5 3L18.75 4.5',
        stroke: '#0095FF',
        'stroke-width': '1.5',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
      }),
      h('path', {
        d: 'M5.25 19.5L8.5 21L15.5 18L18.75 19.5',
        stroke: '#0095FF',
        'stroke-width': '1.5',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
      })
    ]
  );

const IconSubway = () =>
  h(
    'svg',
    {
      xmlns: 'http://www.w3.org/2000/svg',
      width: '24',
      height: '24',
      viewBox: '0 0 24 24',
      fill: 'none'
    },
    [
      h('path', {
        d: 'M4.92893 16.3787C3.11929 16.9216 2 17.6716 2 18.5C2 20.1569 6.47715 21.5 12 21.5C17.5229 21.5 22 20.1569 22 18.5C22 17.6716 20.8807 16.9216 19.0711 16.3787',
        stroke: '#0095FF',
        'stroke-width': '1.5',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
      }),
      h('path', {
        d: 'M12 17.5C12 17.5 18.5 13.252 18.5 8.3409C18.5 4.83892 15.5898 2 12 2C8.41015 2 5.5 4.83892 5.5 8.3409C5.5 13.252 12 17.5 12 17.5Z',
        stroke: '#0095FF',
        'stroke-width': '1.5',
        'stroke-linejoin': 'round'
      }),
      h('path', {
        d: 'M12 11C13.3807 11 14.5 9.8807 14.5 8.5C14.5 7.1193 13.3807 6 12 6C10.6193 6 9.5 7.1193 9.5 8.5C9.5 9.8807 10.6193 11 12 11Z',
        stroke: '#0095FF',
        'stroke-width': '1.5',
        'stroke-linejoin': 'round'
      })
    ]
  );

const IconPhone = () =>
  h(
    'svg',
    {
      xmlns: 'http://www.w3.org/2000/svg',
      width: '24',
      height: '24',
      viewBox: '0 0 24 24',
      fill: 'none'
    },
    [
      h('path', {
        d: 'M20.8901 10.3033C21.0165 9.4554 20.9751 8.58735 20.7658 7.7527C20.4893 6.6501 19.9199 5.6059 19.0577 4.74365C18.1954 3.88141 17.1512 3.31205 16.0487 3.03558C15.214 2.82629 14.3459 2.78484 13.498 2.91123',
        stroke: '#0095FF',
        'stroke-width': '1.5',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
      }),
      h('path', {
        d: 'M17.0953 9.90173C17.2307 8.99343 16.9488 8.03493 16.2496 7.33573C15.5503 6.63653 14.5918 6.35458 13.6836 6.48998',
        stroke: '#0095FF',
        'stroke-width': '1.5',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
      }),
      h('path', {
        d: 'M7.18735 4.39709C7.55065 4.39709 7.88535 4.5941 8.06165 4.91172L9.2849 7.11513C9.44505 7.40363 9.45255 7.75258 9.305 8.04773L8.1266 10.4045C8.1266 10.4045 8.4681 12.1603 9.8973 13.5895C11.3265 15.0187 13.0764 15.3543 13.0764 15.3543L15.4328 14.1761C15.7282 14.0284 16.0774 14.036 16.366 14.1965L18.5756 15.425C18.8929 15.6014 19.0897 15.9359 19.0897 16.299V18.8357C19.0897 20.1276 17.8898 21.0606 16.6657 20.6476C14.1518 19.7993 10.2494 18.1842 7.776 15.7108C5.30255 13.2374 3.68745 9.33503 2.83918 6.82108C2.42617 5.59703 3.35922 4.39709 4.65104 4.39709H7.18735Z',
        stroke: '#0095FF',
        'stroke-width': '1.5',
        'stroke-linejoin': 'round'
      }),
    ]
  );

const contactItems = [
  { title: '总部地址', text: ADDRESS, icon: IconBuilding },
  { title: '地铁交通', text: TRANSPORT, icon: IconSubway },
  { title: '联系电话', text: PHONE_LINE, icon: IconPhone }
];

const form = reactive({
  name: '',
  company: '',
  phone: '',
  inquiryTypes: [] as string[],
  description: ''
});

const submitting = ref(false);
const formError = ref('');
const formSuccess = ref('');

function resetForm() {
  form.name = '';
  form.company = '';
  form.phone = '';
  form.inquiryTypes = [];
  form.description = '';
}

function buildGuestMessage(): string {
  return JSON.stringify({
    name: form.name,
    company: form.company,
    phone: form.phone,
    inquiryTypes: form.inquiryTypes,
    description: form.description
  });
}

function validateForm(): string | null {
  if (!form.name) return '请填写姓名';
  if (!form.phone) return '请填写联系电话';
  if (!form.inquiryTypes.length) return '请至少选择一种咨询类型';
  return null;
}

async function onSubmit() {
  formError.value = '';
  formSuccess.value = '';
  const err = validateForm();
  if (err) {
    formError.value = err;
    return;
  }
  submitting.value = true;
  try {
    await fetchContactUsMessages({
      contact: form.phone,
      message: buildGuestMessage()
    });
    formSuccess.value = '提交成功，我们将尽快与您联系。';
    resetForm();
  } catch (e) {
    formError.value = e instanceof Error ? e.message : '提交失败，请稍后重试';
  } finally {
    submitting.value = false;
  }
}

useSeo({
  title: '联系我们',
  description: '海曦联系方式、总部地址与在线留言入口。'
});
</script>

<style scoped lang="scss">
.contact-page {
  position: relative;
  padding: 120px 0 80px;
  background: #f7fbff;
  overflow: hidden;
  min-height: calc(100vh - 156px);

}

.contact-page-container {
  width: min(1120px, calc(100% - 48px));
  margin: 0 auto;
}

.contact-wave {
  position: absolute;
  inset: 0 0 auto;
  height: 420px;
  background:
    radial-gradient(ellipse 120% 80% at 50% -20%, rgba(0, 149, 255, 0.08) 0%, transparent 55%),
    linear-gradient(180deg, rgba(0, 149, 255, 0.04) 0%, transparent 100%);
  pointer-events: none;
}

.contact-top,
.contact-form-section {
  position: relative;
  z-index: 1;
}

.contact-top__head {
  margin-bottom: 40px;
}

.contact-top__title {
  margin: 0;
  font-size: clamp(32px, 4vw, 44px);
  font-weight: 600;
  line-height: 1.2;
  color: #1a1a1a;
}

.contact-top__eyebrow {
  color: rgba(153, 153, 153, 0.60);
  font-size: 24px;
}

.contact-top__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: stretch;
}

.contact-map-link {
  position: relative;
  display: block;
  margin-bottom: 28px;
  border-radius: 8px;
  overflow: hidden;
  line-height: 0;
  text-decoration: none;
  cursor: pointer;
  transition:
    transform 0.25s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(0, 149, 255, 0.18);

    .contact-map-link__badge {
      background: rgba(0, 149, 255, 0.92);
    }
  }

  &:focus-visible {
    outline: 2px solid #0095ff;
    outline-offset: 3px;
  }
}

.contact-map-link__img {
  display: block;
  width: 100%;
  height: auto;
}

.contact-map-link__badge {
  position: absolute;
  right: 12px;
  bottom: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  background: rgba(0, 149, 255, 0.82);
  backdrop-filter: blur(6px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
  transition: background 0.2s ease;
}

.contact-map-link__icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.contact-top__building {
  position: relative;
  width: 100%;
  min-height: 420px;
  border-radius: 8px;
  overflow: hidden;
  background: lightgray;

  &::after {
    content: '';
    position: absolute;
    inset: auto 0 0;
    height: 42%;
    background: linear-gradient(180deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.55) 38%,
        rgba(255, 255, 255, 0.92) 72%,
        #f7fbff 100%);
    pointer-events: none;
  }
}

.contact-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;

  &--building {
    width: 100%;
    height: 100%;
    min-height: 420px;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      inset: auto 0 0;
      height: 35%;
      background: linear-gradient(180deg, transparent 0%, #f7fbff 100%);
      pointer-events: none;
    }
  }

  &--map {
    position: relative;
    width: 100%;
    height: 220px;
    margin-bottom: 28px;
    background: linear-gradient(160deg, #e8f4fc 0%, #dbeafe 100%);
  }

  &--handshake {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 360px;

    &::after {
      content: '';
      position: absolute;
      inset: 0 0 auto;
      z-index: 1;
      height: 48%;
      background: linear-gradient(180deg,
          rgba(255, 255, 255, 0.98) 0%,
          rgba(255, 255, 255, 0.72) 32%,
          rgba(255, 255, 255, 0.28) 62%,
          rgba(255, 255, 255, 0) 100%);
      pointer-events: none;
    }

    .contact-handshake__img {
      display: block;
      width: 100%;
      height: 100%;
      min-height: 360px;
      object-fit: cover;
    }
  }

  &__label {
    font-size: 14px;
    user-select: none;

    &--sm {
      font-size: 11px;
    }
  }
}

.contact-map__pin {
  position: absolute;
  top: 42%;
  left: 52%;
  width: 18px;
  height: 18px;
  margin: -18px 0 0 -9px;
  border-radius: 50% 50% 50% 0;
  background: #ef4444;
  transform: rotate(-45deg);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.45);

  &::after {
    content: '';
    position: absolute;
    inset: 4px;
    border-radius: 50%;
    background: #fff;
  }
}

.contact-info {
  margin: 0;
  padding: 0;
  list-style: none;
}

.contact-info__row {
  display: flex;
  gap: 16px;
  align-items: flex-start;

  padding: 18px;
  border-bottom: 1px solid #eef0f4;

  &:last-child {
    border-bottom: none;
  }
}

.contact-info__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  color: #0095ff;
  background: rgba(0, 149, 255, 0.1);

  :deep(svg) {
    width: 22px;
    height: 22px;
  }
}

.contact-info__body {
  min-width: 0;
}

.contact-info__title {
  display: block;
  margin-bottom: 6px;
  font-size: 15px;
  font-weight: 600;
}

.contact-info__text {
  margin: 0;
  font-size: 14px;
  line-height: 1.65;
  color: #666;
}

.contact-visit-note {
  margin: 20px 0 0;
  font-size: 14px;
  line-height: 1.75;
  color: #666;
}

.contact-form-section {
  margin-top: 72px;
}

.contact-form-section__head {
  max-width: 720px;
  margin-bottom: 36px;
}

.contact-form-section__title {
  margin: 0;
  font-size: clamp(22px, 2.5vw, 28px);
  font-weight: 600;
  line-height: 1.35;
  color: #1a1a1a;
}

.contact-form-section__desc {
  margin: 14px 0 0;
  font-size: 14px;
  line-height: 1.75;
  color: #6b7280;
}

.contact-form-section__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: stretch;
}



.inquiry-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;

  &--types {
    align-items: center;
  }

  &--desc {
    align-items: flex-start;
  }
}

.inquiry-label {
  flex: 0 0 96px;
  margin: 8px 0 0;
  font-size: 14px;
  text-align: left;
  line-height: 1.4;
}

.inquiry-required {
  color: #ef4444;
  margin-right: 2px;
}

.inquiry-input,
.inquiry-textarea {
  flex: 1;
  min-width: 0;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font: inherit;
  font-size: 14px;
  color: #333;
  background: #fff;
  outline: none;
  transition: border-color 0.2s ease;
  height: 40px;

  &:focus {
    border-color: #0095ff;
  }
}

.inquiry-input {
  height: 36px;
}


.inquiry-types {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px 16px;
  min-width: 0;
}

.inquiry-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}

.inquiry-check-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.inquiry-check-box {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border: 1px solid #c0c4cc;
  border-radius: 2px;
  background: #fff;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;
}

.inquiry-check-input:checked+.inquiry-check-box {
  border-color: #0095ff;
  background: #0095ff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 10'%3E%3Cpath fill='none' stroke='%23fff' stroke-width='2' d='M1 5l3 3 7-7'/%3E%3C/svg%3E") center / 10px 8px no-repeat;
}

.inquiry-check-text {
  font-size: 14px;
  color: #333;
  line-height: 1.4;
}

.inquiry-feedback {
  margin: 0 0 12px 112px;
  font-size: 13px;

  &--error {
    color: #e53935;
  }

  &--success {
    color: #2e7d32;
  }
}

.inquiry-actions {
  margin-bottom: 16px;
}

.inquiry-submit {
  height: 38px;
  padding: 0 16px;
  border: none;
  border-radius: 4px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  background: #0095ff;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    opacity 0.2s ease;

  &:hover:not(:disabled) {
    background: #0084e6;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}

.inquiry-privacy {
  margin: 0;
  font-size: 14px;
  line-height: 1.75;
  color: #666;
}

.contact-form-visual {
  position: relative;
  min-height: 360px;
  overflow: hidden;
  background: #eef2f7;
}

@media (max-width: 960px) {
  .contact-page {
    padding: 100px 0 56px;
  }

  .contact-top__grid,
  .contact-form-section__grid {
    grid-template-columns: 1fr;
  }

  .contact-top__building {
    min-height: 280px;
  }

  .contact-placeholder--building {
    min-height: 280px;
  }

  .contact-form-visual {
    min-height: 260px;
    order: -1;
  }

  .inquiry-form {
    padding: 28px 24px 24px;
  }
}

@media (max-width: 640px) {
  .inquiry-row {
    flex-direction: column;
    gap: 8px;
    margin-bottom: 18px;
  }

  .inquiry-label {
    flex: none;
    width: 100%;
    margin: 0;
    text-align: left;
  }

  .inquiry-types {
    grid-template-columns: 1fr;
  }

  .inquiry-feedback,
  .inquiry-actions,
  .inquiry-privacy {
    padding-left: 0;
    margin-left: 0;
  }
}
</style>
